import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import { formatUsd } from "@/admin/shared/utils";
import type { DisputeRow } from "./types";

const statusChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  NEEDS_RESPONSE: { label: "Требует ответа", color: "warning" },
  WON: { label: "Выигран", color: "success" },
  LOST: { label: "Проигран", color: "error" },
  CLOSED: { label: "Закрыт", color: "default" },
};

// Тот же локальный маппинг типа продукта, что и в feature productPurchases —
// admin не переиспользует partner-локализацию, здесь нет i18n.
const typeLabel: Record<string, string> = {
  PPV: "Разовая покупка",
  SUBSCRIPTION: "Подписка",
};

function renderStatus(status: string) {
  const { label, color } = statusChipConfig[status] ?? {
    label: status,
    color: "default" as const,
  };
  return <Chip label={label} color={color} size="small" />;
}

function renderBuyer(dispute: DisputeRow) {
  const { payment } = dispute;
  if (payment?.userId) {
    return (
      <CrudLink path={PagePaths.users} tab="details" id={payment.userId}>
        {payment.buyerEmail ?? "—"}
      </CrudLink>
    );
  }
  return payment?.buyerEmail ?? "—";
}

export const getColumnsConfig = (): ColumnDefinition<DisputeRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "amount",
    label: "Сумма",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const amount = data as DisputeRow["amount"];
        return `${amount.value} ${amount.currency}`;
      },
    },
  },
  {
    name: "amountUSD",
    label: "USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "ottkitFeeUSD",
    label: "Комиссия USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "organizationUSD",
    label: "Доход организации USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "buyer",
    label: "Покупатель",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => renderBuyer(data as DisputeRow),
    },
  },
  {
    name: "product",
    label: "Продукт",
    options: {
      sort: false,
      // Продукты admin-only не редактируются (нет админ-поверхности
      // управления продуктами) — ссылки нет, только текст.
      customBodyRender: (data: unknown) => {
        const dispute = data as DisputeRow;
        return dispute.payment?.product?.title ?? "—";
      },
    },
  },
  {
    name: "provider",
    label: "Провайдер",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const dispute = data as DisputeRow;
        return dispute.payment?.provider ?? "—";
      },
    },
  },
  {
    name: "type",
    label: "Тип",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const dispute = data as DisputeRow;
        const type = dispute.payment?.product?.type;
        if (!type) return "—";
        return typeLabel[type] ?? type;
      },
    },
  },
  {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) => renderStatus(value as string),
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const dispute = data as DisputeRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={dispute.organizationId}
          >
            {dispute.organization?.title ?? "—"}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "createdAt",
    label: "Дата",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "closedAt",
    label: "Дата закрытия",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const dateValue = value as string | null;
        return dateValue ? <CustomCellDate date={dateValue} /> : "—";
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Диспуты",
  refetchList: "Обновить список",
});

export const getMobileColumnsConfig = (): DataListViewColumns<DisputeRow> => ({
  main: {
    title: "Диспут",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <Typography noWrap>
          {row.amount.value} {row.amount.currency}
        </Typography>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.organization?.title ?? "—"}
        </Typography>
      </Stack>
    ),
  },
  status: {
    title: "Статус",
    sx: { width: 132, flexShrink: 0 },
    renderCell: (row) => renderStatus(row.status),
  },
});
